import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { QrCode, RefreshCw, Route, ScanLine, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import Input from '@/components/ui/input.jsx';
import Select from '@/components/ui/select.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  confirmDispatch,
  getFojaAppSummary,
  getFojaDetail,
  listFojas,
} from '@/services/fojas.js';

const STATE_VARIANTS = {
  cerrada: 'warning',
  despachada_confirmada: 'default',
  recibida: 'default',
  recibida_parcial: 'warning',
};

const RECEPCION_MODOS = [
  { value: 'manual_por_guia', label: 'Manual por guia' },
  { value: 'marcar_todo', label: 'Marcar todo' },
  { value: 'qr_obligatorio', label: 'QR obligatorio' },
];

const formatDateTime = (value) => {
  if (!value) return 'Pendiente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const formatStateLabel = (value) => String(value || 'sin_estado').replaceAll('_', ' ');

const getSucursalInfo = (auth) => {
  const sucursal = auth?.sucursalActiva || auth?.sucursal || null;
  return {
    id: sucursal?.id || sucursal?.sucursal_id || null,
    nombre: sucursal?.nombre || sucursal?.descripcion || 'Sucursal activa',
  };
};

const compactMetaCardClassName =
  'rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2.5';

const metricCardClassName =
  'rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.64)] px-3 py-2.5';

function InfoCell({ label, value, emphasize = false }) {
  return (
    <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">{label}</p>
      <p className={`mt-1 text-sm ${emphasize ? 'font-semibold text-[rgb(var(--fg))]' : 'text-[rgba(var(--fg),0.82)]'}`}>
        {value}
      </p>
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className={metricCardClassName}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">{label}</p>
      <p className="mt-1 text-lg font-semibold text-[rgb(var(--fg))]">{value}</p>
    </div>
  );
}

export default function ConfirmacionDeDespachos() {
  const auth = useAuth();
  const sucursal = getSucursalInfo(auth);

  const [fojas, setFojas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [appSummary, setAppSummary] = useState(null);
  const [form, setForm] = useState({
    modo_recepcion: 'manual_por_guia',
    observaciones: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const pendingDispatch = useMemo(
    () => fojas.filter((item) => item.estado === 'cerrada'),
    [fojas],
  );

  const selectedFoja = detail?.foja || pendingDispatch.find((item) => Number(item.id) === Number(selectedId)) || null;
  const isConfirmed = selectedFoja?.estado === 'despachada_confirmada';
  const canConfirm = selectedFoja?.estado === 'cerrada' && !isSubmitting;
  const includedGuias = detail?.guias || [];
  const qrToken = appSummary?.foja?.qr_token || '';
  const stickySummary = selectedFoja
    ? `Guias ${formatNumber(selectedFoja.total_guias || selectedFoja.total_guias_rel, 0)} · Bultos ${formatNumber(selectedFoja.total_bultos, 0)} · Kg ${formatNumber(selectedFoja.total_kilos, 2)}`
    : 'Selecciona una foja para habilitar la confirmacion.';

  const loadFojas = async ({ keepSelection = true } = {}) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const data = await listFojas({ sucursal_id: sucursal.id, limit: 200 });
      const rows = Array.isArray(data) ? data.filter((item) => ['cerrada', 'despachada_confirmada'].includes(item.estado)) : [];
      setFojas(rows);
      const nextId = keepSelection && selectedId && rows.some((item) => Number(item.id) === Number(selectedId))
        ? selectedId
        : rows[0]?.id || null;
      setSelectedId(nextId);
    } catch (error) {
      setErrorMessage(error.message || 'No se pudo cargar la cola de despacho.');
      setFojas([]);
      setSelectedId(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!sucursal.id) return;
    void loadFojas({ keepSelection: false });
  }, [sucursal.id]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setAppSummary(null);
      return;
    }
    let ignore = false;
    setErrorMessage('');
    Promise.all([getFojaDetail(selectedId), getFojaAppSummary(selectedId)])
      .then(([detailData, summaryData]) => {
        if (ignore) return;
        setDetail(detailData);
        setAppSummary(summaryData);
        setForm((prev) => ({
          ...prev,
          modo_recepcion: detailData?.foja?.modo_recepcion || prev.modo_recepcion,
          observaciones: detailData?.foja?.observaciones || '',
        }));
      })
      .catch((error) => {
        if (!ignore) setErrorMessage(error.message || 'No se pudo cargar la foja para despacho.');
      });
    return () => {
      ignore = true;
    };
  }, [selectedId]);

  const handleConfirmDispatch = async () => {
    if (!selectedId) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const data = await confirmDispatch(selectedId, {
        modo_recepcion: form.modo_recepcion,
        observaciones: form.observaciones || null,
      });
      setDetail(data);
      const summary = await getFojaAppSummary(selectedId);
      setAppSummary(summary);
      setSuccessMessage('Despacho confirmado. Genera QR operativo y deja trazada la salida para SinamonApp.');
      await loadFojas();
    } catch (error) {
      setErrorMessage(error.message || 'No se pudo confirmar el despacho.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[1520px] flex-col gap-3 px-3 py-2 text-[rgb(var(--fg))] md:px-4 md:py-3">
      <section className="rounded-[26px] border border-[rgba(var(--border),0.42)] bg-[linear-gradient(135deg,rgba(var(--panel),0.98),rgba(var(--subpanel),0.9))] px-4 py-3 shadow-[0_14px_34px_rgba(var(--shadow),0.14)]">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.66)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.72)]">
              <Truck size={13} />
              ST442 - Confirmacion de despacho
            </div>
            <div className="mt-2 flex flex-col gap-1 xl:flex-row xl:items-end xl:gap-3">
              <h1 className="text-xl font-semibold tracking-tight md:text-2xl">Salida real de fojas cerradas</h1>
              <p className="text-xs text-[rgba(var(--fg),0.7)] md:text-sm">
                Confirmacion operativa posterior al cierre documental. La salida real y el QR quedan trazados aqui.
              </p>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 xl:min-w-[460px]">
            <div className={compactMetaCardClassName}>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Sucursal origen</p>
              <p className="mt-1 text-sm font-semibold text-[rgb(var(--fg))]">{sucursal.nombre}</p>
            </div>
            <div className={compactMetaCardClassName}>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Pendientes de despacho</p>
              <p className="mt-1 text-sm font-semibold text-[rgb(var(--fg))]">{formatNumber(pendingDispatch.length, 0)}</p>
            </div>
          </div>
        </div>
      </section>

      {errorMessage ? (
        <div className="rounded-2xl border border-red-400/35 bg-red-500/10 px-3 py-2 text-sm text-red-100">
          {errorMessage}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-emerald-400/35 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-50">
          {successMessage}
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-3 xl:grid-cols-[360px_minmax(0,1fr)]">
        <section className="flex min-h-0 flex-col rounded-[24px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.94)] p-3">
          <div className="flex flex-wrap items-start justify-between gap-2 border-b border-[rgba(var(--border),0.2)] pb-2">
            <div>
              <h2 className="text-base font-semibold">Cola de despacho</h2>
              <p className="mt-0.5 text-xs text-[rgba(var(--fg),0.64)]">
                Scroll interno si hay muchas fojas. Confirmadas siguen visibles como referencia.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => loadFojas({ keepSelection: false })} disabled={isLoading}>
              <RefreshCw size={14} />
              Actualizar
            </Button>
          </div>

          <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
            {!fojas.length ? (
              <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.74)] px-3 py-4 text-sm text-[rgba(var(--fg),0.68)]">
                {isLoading ? 'Cargando cola de despacho...' : 'No hay fojas cerradas o despachadas para esta sucursal.'}
              </div>
            ) : (
              <div className="space-y-2">
                {fojas.map((foja) => (
                  <button
                    key={foja.id}
                    type="button"
                    onClick={() => setSelectedId(foja.id)}
                    className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                      Number(selectedId) === Number(foja.id)
                        ? 'border-[rgba(var(--brand),0.52)] bg-[rgba(var(--brand),0.12)] shadow-[0_8px_22px_rgba(var(--shadow),0.16)]'
                        : 'border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.74)] hover:bg-[rgba(var(--subpanel),0.88)]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold md:text-[15px]">Foja #{foja.numero || foja.id}</p>
                        <p className="mt-0.5 truncate text-xs text-[rgba(var(--fg),0.66)]">
                          {foja.sucursal_origen_nombre || foja.sucursal_origen_id}
                          {' -> '}
                          {foja.sucursal_destino_nombre || foja.sucursal_destino_id}
                        </p>
                      </div>
                      <Badge variant={STATE_VARIANTS[foja.estado] || 'outline'}>
                        {formatStateLabel(foja.estado)}
                      </Badge>
                    </div>
                    <div className="mt-2 grid gap-1 text-xs text-[rgba(var(--fg),0.72)] sm:grid-cols-3">
                      <p>Linea: <strong className="text-[rgb(var(--fg))]">{foja.linea || 'Sin linea'}</strong></p>
                      <p>Guias: <strong className="text-[rgb(var(--fg))]">{formatNumber(foja.total_guias || foja.total_guias_rel, 0)}</strong></p>
                      <p>Cierre: <strong className="text-[rgb(var(--fg))]">{formatDateTime(foja.fecha_cierre)}</strong></p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="flex min-h-0 flex-col rounded-[24px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.94)] p-3">
          {!selectedFoja ? (
            <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.76)] px-4 py-5 text-sm text-[rgba(var(--fg),0.68)]">
              Selecciona una foja cerrada para confirmar la salida real.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-2 border-b border-[rgba(var(--border),0.2)] pb-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold md:text-lg">Foja #{selectedFoja.numero || selectedFoja.id}</h2>
                    <Badge variant={STATE_VARIANTS[selectedFoja.estado] || 'outline'}>
                      {formatStateLabel(selectedFoja.estado)}
                    </Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-[rgba(var(--fg),0.66)] md:text-sm">
                    {selectedFoja.sucursal_origen_nombre || selectedFoja.sucursal_origen_id}
                    {' -> '}
                    {selectedFoja.sucursal_destino_nombre || selectedFoja.sucursal_destino_id}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => loadFojas()} disabled={isSubmitting}>
                    <RefreshCw size={14} />
                    Refrescar
                  </Button>
                  {selectedFoja.estado === 'despachada_confirmada' ? (
                    <Link
                      to={`/fojas/recepcion/${selectedFoja.id}`}
                      className="inline-flex h-8 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] px-3 text-sm font-semibold hover:bg-[rgba(var(--subpanel),0.82)]"
                    >
                      <ScanLine size={14} />
                      Ir a recepcion
                    </Link>
                  ) : null}
                  <Link
                    to="/cargas/fojas-de-ruta"
                    className="inline-flex h-8 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] px-3 text-sm font-semibold hover:bg-[rgba(var(--subpanel),0.82)]"
                  >
                    <Route size={14} />
                    Ver consulta
                  </Link>
                </div>
              </div>

              <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1 pb-24">
                <div className="space-y-3">
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                    <InfoCell label="Foja" value={`#${selectedFoja.numero || selectedFoja.id}`} emphasize />
                    <InfoCell
                      label="Tramo"
                      value={`${selectedFoja.sucursal_origen_nombre || selectedFoja.sucursal_origen_id} -> ${selectedFoja.sucursal_destino_nombre || selectedFoja.sucursal_destino_id}`}
                    />
                    <InfoCell label="Estado" value={formatStateLabel(selectedFoja.estado)} emphasize />
                    <InfoCell
                      label="Linea / via"
                      value={`${selectedFoja.linea || 'Sin linea'} · ${selectedFoja.sucursal_via_nombre || 'Directo'}`}
                    />
                    <InfoCell
                      label="Chofer / unidad"
                      value={`${selectedFoja.chofer_id ? `Chofer #${selectedFoja.chofer_id}` : 'Sin chofer'} · ${selectedFoja.vehiculo_id || selectedFoja.unidad_id ? `Unidad #${selectedFoja.vehiculo_id || selectedFoja.unidad_id}` : 'Sin unidad'}`}
                    />
                    <InfoCell label="Cierre documental" value={formatDateTime(selectedFoja.fecha_cierre)} />
                    <InfoCell
                      label="Precintos"
                      value={[selectedFoja.precinto_1, selectedFoja.precinto_2, selectedFoja.precinto_3].filter(Boolean).join(' · ') || 'Sin precintos'}
                    />
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    <MetricCell label="Guias" value={formatNumber(selectedFoja.total_guias || selectedFoja.total_guias_rel, 0)} />
                    <MetricCell label="Bultos" value={formatNumber(selectedFoja.total_bultos, 0)} />
                    <MetricCell label="Kg" value={formatNumber(selectedFoja.total_kilos, 2)} />
                    <MetricCell label="Valor declarado" value={`$${formatNumber(selectedFoja.total_valor_declarado, 2)}`} />
                  </div>

                  <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2.5">
                    <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
                      <div className="min-w-0 xl:w-[220px]">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Modo de recepcion esperado</p>
                        <p className="mt-1 text-xs text-[rgba(var(--fg),0.68)]">
                          Define como se espera validar la recepcion y deja el token o nota corta si corresponde.
                        </p>
                      </div>

                      <div className="grid flex-1 gap-2 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)_auto]">
                        <Select
                          value={form.modo_recepcion}
                          onChange={(event) => setForm((prev) => ({ ...prev, modo_recepcion: event.target.value }))}
                          disabled={!canConfirm}
                        >
                          {RECEPCION_MODOS.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </Select>

                        <Input
                          value={isConfirmed && qrToken ? qrToken : form.observaciones}
                          onChange={(event) => setForm((prev) => ({ ...prev, observaciones: event.target.value }))}
                          placeholder={form.modo_recepcion === 'qr_obligatorio' ? 'Token o referencia QR operativa' : 'Observacion corta, horario o novedad'}
                          disabled={isConfirmed || isSubmitting}
                          readOnly={isConfirmed && Boolean(qrToken)}
                        />

                        <div className="flex items-center justify-start">
                          {isConfirmed && qrToken ? (
                            <div className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 text-xs font-semibold uppercase tracking-[0.12em] text-emerald-100">
                              <QrCode size={14} />
                              QR operativo listo
                            </div>
                          ) : (
                            <div className="inline-flex h-10 items-center rounded-xl border border-[rgba(var(--border),0.3)] px-3 text-xs text-[rgba(var(--fg),0.68)]">
                              Escaneo o carga breve
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2.5">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Base movil / QR</p>
                        <p className="mt-1 text-xs text-[rgba(var(--fg),0.7)]">
                          {isConfirmed
                            ? 'Salida confirmada. El token QR ya puede ser usado por SinamonApp.'
                            : 'Al confirmar, se genera el QR operativo y queda trazada la salida real.'}
                        </p>
                      </div>

                      {isConfirmed && qrToken ? (
                        <div className="max-w-full rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.62)] px-3 py-2 text-xs text-[rgba(var(--fg),0.74)] break-all md:max-w-[420px]">
                          {qrToken}
                        </div>
                      ) : null}
                    </div>

                    {isConfirmed && appSummary ? (
                      <div className="mt-2 grid gap-2 text-xs text-[rgba(var(--fg),0.74)] md:grid-cols-3">
                        <p>Modo: <strong className="text-[rgb(var(--fg))]">{formatStateLabel(appSummary.modo_recepcion)}</strong></p>
                        <p>Esperadas: <strong className="text-[rgb(var(--fg))]">{formatNumber(appSummary.progreso?.esperadas, 0)}</strong></p>
                        <p>Pendientes scan: <strong className="text-[rgb(var(--fg))]">{formatNumber(appSummary.pendientes_scan?.length, 0)}</strong></p>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.76)] px-3 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Guias incluidas</p>
                      <p className="text-xs text-[rgba(var(--fg),0.62)]">{formatNumber(includedGuias.length, 0)} registros</p>
                    </div>
                    <div className="mt-2 overflow-x-auto rounded-xl border border-[rgba(var(--border),0.22)]">
                      <table className="min-w-full border-collapse text-sm">
                        <thead className="bg-[rgba(var(--panel),0.72)] text-left text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">
                          <tr>
                            <th className="px-3 py-2">Guia</th>
                            <th className="px-3 py-2">Estado</th>
                            <th className="px-3 py-2">Cliente</th>
                            <th className="px-3 py-2 text-right">Bultos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {includedGuias.map((item) => (
                            <tr key={item.guia_id} className="border-t border-[rgba(var(--border),0.22)]">
                              <td className="px-3 py-2 font-semibold">{item.numero_guia || item.guia_id}</td>
                              <td className="px-3 py-2">{item.estado}</td>
                              <td className="px-3 py-2">{item.destinatario_nombre || item.remitente_nombre || '-'}</td>
                              <td className="px-3 py-2 text-right">{formatNumber(item.bultos, 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleConfirmDispatch} disabled={!canConfirm}>
                      {isSubmitting ? 'Confirmando...' : 'Confirmar salida real'}
                    </Button>
                    {isConfirmed ? (
                      <Button variant="outline" disabled>
                        Salida ya confirmada
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <div className="sticky bottom-0 z-20 -mx-1 pb-1">
        <div className="rounded-[22px] border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.94)] px-3 py-2.5 shadow-[0_-10px_30px_rgba(var(--shadow),0.22)] backdrop-blur">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="grid gap-2 md:grid-cols-[minmax(0,160px)_minmax(0,180px)_minmax(0,1fr)]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Foja seleccionada</p>
                <p className="mt-0.5 text-sm font-semibold text-[rgb(var(--fg))]">
                  {selectedFoja ? `#${selectedFoja.numero || selectedFoja.id}` : 'Sin seleccion'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Estado actual</p>
                <p className="mt-0.5 text-sm font-semibold text-[rgb(var(--fg))]">
                  {selectedFoja ? formatStateLabel(selectedFoja.estado) : 'Pendiente'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.48)]">Resumen breve</p>
                <p className="mt-0.5 text-sm text-[rgba(var(--fg),0.78)]">{stickySummary}</p>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <p className="max-w-[360px] text-xs text-[rgba(var(--fg),0.68)]">
                Genera QR operativo y deja trazada la salida para SinamonApp.
              </p>
              <Button onClick={handleConfirmDispatch} disabled={!canConfirm} className="min-w-[220px]">
                {isConfirmed ? 'Salida ya confirmada' : isSubmitting ? 'Confirmando...' : 'Confirmar salida real'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
